import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient } from '@/lib/api';

// Task #1984 round B / #2997 (T8) — picker condiviso per il provider 'custom'
// (endpoint OpenAI-compatible registrati in Custom AI). Estratto da
// LLMProviderConfig per riuso (Agenti AI + Architetto) senza duplicazione.
interface CustomEndpointLite { id: string; name: string; provider_type: string; is_active: boolean; }
interface CustomModelLite { id: string; model_name: string; display_name: string | null; is_active: boolean; }
export interface CustomLLMPickerProps {
  endpointId: string | null;
  modelId: string | null;
  modelName: string;
  onChange: (endpointId: string | null, modelId: string | null, modelName: string) => void;
}

export function CustomLLMPicker({ endpointId, modelId, modelName, onChange }: CustomLLMPickerProps): React.ReactElement {
  const [endpoints, setEndpoints] = useState<CustomEndpointLite[]>([]);
  const [models, setModels] = useState<CustomModelLite[]>([]);
  const [loadingEp, setLoadingEp] = useState(false);
  const [loadingMd, setLoadingMd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingEp(true);
    apiClient.get<{ endpoints: CustomEndpointLite[] }>('/custom-ai/endpoints')
      .then((res) => {
        if (cancelled) return;
        const data = (res as { data?: { endpoints?: CustomEndpointLite[] } })?.data?.endpoints
          ?? (res as { endpoints?: CustomEndpointLite[] })?.endpoints
          ?? [];
        setEndpoints(Array.isArray(data) ? data.filter((e) => e.is_active) : []);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message || 'Errore caricamento endpoint'); })
      .finally(() => { if (!cancelled) setLoadingEp(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!endpointId) { setModels([]); return; }
    let cancelled = false;
    setLoadingMd(true);
    apiClient.get<{ models: CustomModelLite[] }>(`/custom-ai/endpoints/${endpointId}/models`)
      .then((res) => {
        if (cancelled) return;
        const data = (res as { data?: { models?: CustomModelLite[] } })?.data?.models
          ?? (res as { models?: CustomModelLite[] })?.models
          ?? [];
        setModels(Array.isArray(data) ? data.filter((m) => m.is_active) : []);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message || 'Errore caricamento modelli'); })
      .finally(() => { if (!cancelled) setLoadingMd(false); });
    return () => { cancelled = true; };
  }, [endpointId]);

  if (loadingEp) {
    return <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Caricamento endpoint custom...</div>;
  }
  if (endpoints.length === 0) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-xs">
        <p className="font-medium text-amber-800 dark:text-amber-200">Nessun endpoint custom configurato</p>
        <p className="text-amber-700 dark:text-amber-300 mt-1">Vai in <strong>AI Tools → Custom AI</strong> per registrare un endpoint OpenAI-compatible (Ollama, vLLM, RunPod, LM Studio, Together, Groq, Mistral).</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Endpoint custom</Label>
        <Select value={endpointId || ''} onValueChange={(v) => onChange(v, null, '')}>
          <SelectTrigger className="text-sm"><SelectValue placeholder="Seleziona endpoint" /></SelectTrigger>
          <SelectContent>
            {endpoints.map((ep) => (
              <SelectItem key={ep.id} value={ep.id}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{ep.provider_type}</Badge>
                  <span>{ep.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {endpointId && (
        <div className="space-y-1">
          <Label className="text-xs">Modello</Label>
          {loadingMd ? (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Caricamento modelli...</div>
          ) : models.length === 0 ? (
            <p className="text-xs text-amber-700 dark:text-amber-300">Nessun modello registrato per questo endpoint. Aprilo in AI Tools → Custom AI e fai discovery.</p>
          ) : (
            <Select value={modelId || ''} onValueChange={(v) => {
              const found = models.find((m) => m.id === v);
              onChange(endpointId, v, found?.model_name || '');
            }}>
              <SelectTrigger className="text-sm"><SelectValue placeholder="Seleziona modello" /></SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.display_name || m.model_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {modelName && !modelId && (
            <p className="text-[10px] text-gray-500">Modello corrente: <code>{modelName}</code> (riferimento non risolto)</p>
          )}
        </div>
      )}
      {error && <p className="text-[10px] text-red-600">{error}</p>}
    </div>
  );
}
